import { Output, Mp4OutputFormat, BufferTarget, CanvasSource, AudioBufferSource } from 'mediabunny'
import {
    getMediaTrack,
    getActiveClipAtMs,
    mapEffectiveMsToSourceMs,
    computeTimelineDurationMs
} from '../timelineModel.js'
import { getTransitionAt } from '../compositor/transitions.js'
import { renderClipFrame, renderTransitionFrame, renderOverlayLayer } from '../compositor/canvasCompositor.js'
import { renderMixedAudioBuffer } from '../audio/audioMixer.js'
import { OfflineOverlayRenderer } from '../compositor/overlayRenderer.js'
import { computeVideoBitrate, AUDIO_BITRATE, configureHighQualityContext } from './qualitySettings.js'

// requestVideoFrameCallback doesn't reliably fire on a <video> that's never part of the render
// tree - a fully detached element (created but never appended anywhere) can go without it firing
// at all, which is what made waiting on it turn every seek into an 800ms stall (see seekVideoTo).
// A 1x1px, off-screen (not display:none - that also drops it from the render/paint pipeline) host
// keeps each video "on stage" just enough for the browser to keep presenting its decoded frames.
const createHiddenStage = () => {
    const stage = document.createElement('div')
    stage.style.position = 'fixed'
    stage.style.left = '-99999px'
    stage.style.top = '0'
    stage.style.width = '1px'
    stage.style.height = '1px'
    stage.style.overflow = 'hidden'
    stage.style.pointerEvents = 'none'
    document.body.appendChild(stage)
    return stage
}

const loadMedia = async (source, stage) => {
    let el
    if (source.kind === 'image') {
        el = document.createElement('img')
        el.crossOrigin = 'anonymous'
        el.src = source.url
        await new Promise((resolve, reject) => {
            el.onload = resolve
            el.onerror = reject
        })
    } else {
        el = document.createElement('video')
        el.crossOrigin = 'anonymous'
        el.muted = true
        el.playsInline = true
        el.preload = 'auto'
        el.src = source.url
        stage.appendChild(el)
        await new Promise((resolve) => { el.onloadeddata = resolve })
    }
    return { kind: source.kind, el }
}

// A hard timeout fallback, not just the 'seeked' event: a freshly-created <video> element (one
// per clip - see the mediaPool comment below) can fail to ever fire 'seeked' for its very first
// seek in some browsers, which without this left the whole export loop hanging forever on a
// single frame. Same defensive pattern already needed for the same reason in thumbnails.js's
// filmstrip seeking and TimelinePlayer.js's live-preview seeking.
const hardSeekTo = (entry, targetMs) => {
    const el = entry.el
    el.pause()
    return new Promise((resolve) => {
        const target = targetMs / 1000
        if (Math.abs(el.currentTime - target) < 0.001) { resolve(); return }
        let done = false
        const finish = () => {
            if (done) return
            done = true
            el.removeEventListener('seeked', finish)
            clearTimeout(timeoutId)
            resolve()
        }
        el.addEventListener('seeked', finish)
        el.currentTime = target
        // Raised alongside advanceTo's own MAX_FRAME_WAITS/hangTimeoutId - a real seek here (new
        // clip/transition/silence-skip, see SEEK_JUMP_THRESHOLD_MS) can legitimately take longer
        // than 800ms under the same decode contention (see MAX_FRAME_WAITS's doc); giving up early
        // resolves with whatever position the seek happened to reach so far, not the target - a
        // frame captured there is genuinely wrong (not just late), which is worse than the export
        // simply taking longer.
        const timeoutId = setTimeout(finish, 2500)
    })
}

// How fast the decoder is allowed to race ahead of the source's own real-time playback speed
// while scanning forward for the next needed frame. The output's own pacing is entirely
// independent of this (it's driven by mapEffectiveMsToSourceMs/clip.playbackRate and the fixed
// videoSource.add() timestamps below) - this only controls how fast we're allowed to *look for*
// the next source frame.
// Kept at 1x (not the 16x ceiling HTMLMediaElement.playbackRate technically allows): whatever time
// our own per-frame work (canvas draw + WebCodecs encode, both awaited) takes between two
// requestVideoFrameCallback checks, the video keeps playing in the background for that whole
// stretch - and at a faster rate that overshoot is multiplied by the rate. That's what turned a
// few milliseconds of normal JS overhead into the video racing 5-12 frames past where it should've
// been, sampled unevenly frame to frame - visible as uneven, jittery motion pacing in testing. At
// 1x the overshoot is just the raw JS delay itself (a couple ms, not compounded), which is what
// actually matters here: a several-times-faster-than-realtime export that plays back wrong is far
// worse than a merely real-time-bound one that's correct.
const FAST_SCAN_RATE = 1

// A jump bigger than this (a fresh clip, a transition, silence-removal skipping a chunk, a speed
// change) needs an actual seek - decoding forward frame-by-frame to get there would be needlessly
// slow. Anything smaller is just normal forward playback and is handled by advanceTo below.
const SEEK_JUMP_THRESHOLD_MS = 250

// Repeatedly seeking to ~30-60 arbitrary positions a second (once per output frame) turned out to
// be both slow (each seek can take on the order of 100ms to truly settle) AND inaccurate: 'seeked'
// can fire before the decoder has actually presented the matching image, so the frame drawn was
// often still the previous one - about one output frame in eight came out duplicated in testing.
// Letting the video simply keep playing forward and sampling whichever frame requestVideoFrameCallback
// hands back (checked against its own mediaTime, not just "some new frame arrived") avoids both:
// normal forward decode is what codecs are actually optimized for, and mediaTime tells us for sure
// once we've truly reached the target instead of guessing from 'seeked' timing alone.
//
// This is a *frame-count* budget, not a time one - each wait is one requestVideoFrameCallback firing,
// which only arrives once the browser has actually decoded/presented the next frame. Under CPU
// contention (mediaExportJobs.js serializes the actual encode work for exactly this reason, but the
// rest of the app - Vue re-renders, other tab activity - can still compete for the main thread)
// decode can fall behind wall-clock, so the SAME budget covers less real mediaTime progress than it
// would uncontended - hitting the cap before
// mediaTime actually reaches the target silently accepts an earlier frame than intended, which reads
// as a visible jump/stutter in the export despite every captured frame being genuinely unique (no
// duplicate content for mpdecimate-style detection to catch). Generous on purpose for the same
// reason CUT_PRESEEK_LOOKAHEAD_MS is: this only costs export *time* in the rare contended case,
// never correctness, whereas giving up too early costs correctness in the final file.
const MAX_FRAME_WAITS = 150

const advanceTo = (entry, targetMs) => {
    const el = entry.el
    const deltaMs = targetMs - el.currentTime * 1000

    if (!entry.started || deltaMs < 0 || deltaMs > SEEK_JUMP_THRESHOLD_MS) {
        entry.started = true
        return hardSeekTo(entry, targetMs)
    }

    // requestVideoFrameCallback is tied to the browser's compositor/paint pipeline - it silently
    // stops firing altogether while this tab is backgrounded (switched away to another window/
    // tab), which without this check turned "export keeps running in the background" into
    // "export frozen until the user comes back" (each remaining frame would then just sit on
    // advanceTo's own hangTimeoutId, one at a time). hardSeekTo has no such dependency - it's a
    // pure decode operation driven by the 'seeked' event, not by paint/compositing - so it's used
    // instead whenever the page isn't currently visible, same as when rVFC isn't supported at all.
    if (document.hidden || typeof el.requestVideoFrameCallback !== 'function') {
        return hardSeekTo(entry, targetMs)
    }

    if (el.playbackRate !== FAST_SCAN_RATE) el.playbackRate = FAST_SCAN_RATE
    if (el.paused) el.play().catch(() => {})

    return new Promise((resolve) => {
        let done = false
        const finish = () => {
            if (done) return
            done = true
            el.removeEventListener('ended', finish)
            clearTimeout(hangTimeoutId)
            resolve()
        }

        let waits = 0
        const step = () => {
            if (done) return
            el.requestVideoFrameCallback((now, metadata) => {
                if (done) return
                waits++
                if (metadata.mediaTime * 1000 >= targetMs - 1 || waits >= MAX_FRAME_WAITS) {
                    finish()
                } else {
                    step()
                }
            })
        }
        step()

        // The target can end up a few ms past what the source actually has (rounding in the
        // frame-duration loop, a trim that lands right at the source's last frame...) - once
        // playback truly ends, requestVideoFrameCallback stops firing altogether, and without
        // this the wait above would never resolve. Same idea as hardSeekTo's own timeout: a
        // stalled/unreachable target should still let the export finish instead of hanging on
        // its very last frame forever. Raised alongside MAX_FRAME_WAITS above - under contention a
        // legitimately-progressing wait can now take longer than the old 1500ms without being a
        // genuinely stalled/ended video, so this needs the same headroom or it becomes the binding
        // (and much stricter, being time- not frame-count-based) cap instead.
        el.addEventListener('ended', finish)
        const hangTimeoutId = setTimeout(finish, 4000)
    })
}

const seekVideoTo = (entry, ms) => {
    if (entry.kind === 'image') return Promise.resolve()
    return advanceTo(entry, ms)
}

// Renders the full timeline (clips + transitions + overlays + mixed audio) into a real MP4 file,
// 100% in the browser, via WebCodecs (through mediabunny's high-level CanvasSource/
// AudioBufferSource, so we never touch VideoEncoder/AudioEncoder directly).
// Not real-time: frames are rendered and encoded as fast as the machine allows.
export const exportTimelineToMp4 = async (timeline, { resolveSource, onProgress } = {}) => {
    // AVC requires even dimensions - resizeCanvasToRatio (MediaEditor.vue) already rounds to even
    // for any new ratio/crop, but a timeline saved before that fix (or restored from an older
    // draft) can still carry odd values, so this is rounded again here as a safety net right where
    // the encoder actually enforces it.
    const { fps } = timeline.canvas
    const width = Math.round(timeline.canvas.width / 2) * 2
    const height = Math.round(timeline.canvas.height / 2) * 2
    const durationMs = computeTimelineDurationMs(timeline)
    if (durationMs <= 0) throw new Error('La timeline est vide.')

    const canvasSize = { width, height }
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    configureHighQualityContext(ctx)

    const background = { mode: timeline.canvas.backgroundMode, color: timeline.canvas.backgroundColor }

    const stage = createHiddenStage()
    const mediaPool = new Map()
    let overlayRenderer = null

    try {
        const mediaTrack = getMediaTrack(timeline)
        // Keyed by clip.id (not fileId): a clip split into two halves shares one source file with
        // itself, and a transition between them needs to seek two *different* positions in that
        // file at once - one shared element per fileId can only ever show one of the two.
        for (const clip of mediaTrack.clips) {
            if (!mediaPool.has(clip.id)) {
                const source = resolveSource(clip.fileId)
                if (!source) throw new Error(`Source introuvable pour le clip ${clip.fileId}`)
                mediaPool.set(clip.id, await loadMedia(source, stage))
            }
        }

        const overlayTrack = timeline.tracks.find(t => t.type === 'overlay')
        overlayRenderer = new OfflineOverlayRenderer(canvasSize)
        if (overlayTrack) await overlayRenderer.prepare(overlayTrack.overlays)

        // Mix all audio up front - Output requires every track to be declared before start().
        let mixedAudioBuffer = null
        try {
            mixedAudioBuffer = await renderMixedAudioBuffer(timeline, resolveSource)
        } catch (e) {
            mixedAudioBuffer = null // export still proceeds, silently, without an audio track
        }

        const output = new Output({ format: new Mp4OutputFormat(), target: new BufferTarget() })
        const videoSource = new CanvasSource(canvas, { codec: 'avc', bitrate: computeVideoBitrate(width, height, fps, timeline.exportSettings && timeline.exportSettings.videoBitrate) })
        output.addVideoTrack(videoSource)

        let audioSource = null
        if (mixedAudioBuffer) {
            audioSource = new AudioBufferSource({ codec: 'aac', bitrate: AUDIO_BITRATE })
            output.addAudioTrack(audioSource)
        }

        await output.start()

        const frameDurationMs = 1000 / fps
        for (let t = 0; t < durationMs; t += frameDurationMs) {
            const transition = getTransitionAt(mediaTrack.clips, t)

            if (transition) {
                const fromEntry = mediaPool.get(transition.fromClip.id)
                const toEntry = mediaPool.get(transition.toClip.id)
                const fromSourceMs = mapEffectiveMsToSourceMs(transition.fromClip, t - transition.fromClip.timelineStartMs)
                // Now that the transition window is centered on the cut (see getTransitionAt),
                // t can already be past toClip's own timelineStartMs by the time we're still in
                // the window - Math.max(0, ...) keeps it on its first frame for the pre-cut half,
                // then lets it actually advance for the post-cut half instead of staying frozen
                // on frame 0 for the whole window.
                const toSourceMs = mapEffectiveMsToSourceMs(transition.toClip, Math.max(0, t - transition.toClip.timelineStartMs))
                await Promise.all([seekVideoTo(fromEntry, fromSourceMs), seekVideoTo(toEntry, toSourceMs)])

                renderTransitionFrame(ctx, {
                    fromMediaEl: fromEntry.el,
                    fromClip: transition.fromClip,
                    toMediaEl: toEntry.el,
                    toClip: transition.toClip,
                    canvasSize,
                    type: transition.type,
                    direction: transition.direction,
                    progress: transition.progress,
                    background
                })
            } else {
                const clip = getActiveClipAtMs(timeline, t)
                const entry = mediaPool.get(clip.id)
                const effectiveMs = t - clip.timelineStartMs
                const sourceMs = mapEffectiveMsToSourceMs(clip, effectiveMs)
                await seekVideoTo(entry, sourceMs)

                renderClipFrame(ctx, entry.el, canvasSize, clip, { background })
            }

            if (overlayTrack) {
                const overlayCanvasEl = overlayRenderer.renderAt(overlayTrack.overlays, t)
                renderOverlayLayer(ctx, overlayCanvasEl, canvasSize)
            }

            await videoSource.add(t / 1000, frameDurationMs / 1000)
            if (onProgress) onProgress(Math.min(1, t / durationMs))
        }

        videoSource.close()

        if (audioSource) {
            await audioSource.add(mixedAudioBuffer)
            audioSource.close()
        }

        await output.finalize()

        if (onProgress) onProgress(1)

        return new Blob([output.target.buffer], { type: 'video/mp4' })
    } finally {
        if (overlayRenderer) overlayRenderer.dispose()
        mediaPool.forEach((entry) => {
            if (entry.kind === 'video') {
                entry.el.pause()
                entry.el.removeAttribute('src')
                entry.el.remove()
            }
        })
        stage.remove()
    }
}
